import React, { useState, useEffect } from 'react';
import { 
    UsersIcon, 
    BuildingOffice2Icon, 
    CheckCircleIcon, 
    BuildingOfficeIcon, 
    ChartPieIcon, 
    ChartBarIcon,
    PlusIcon,
    CogIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface AdminStats {
    resumen: {
        totalUsers: number;
        totalProperties: number;
        activeUsers: number;
        activeProperties: number;
    };
    distribucion: {
        usuariosPorTipo: Array<{ _id: string; count: number }>;
        propiedadesPorTipo: Array<{ _id: string; count: number }>;
        propiedadesPorEstado: Array<{ _id: string; count: number }>;
    };
}

const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAdminStats();
    }, []);

    const fetchAdminStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(apiUrl('/api/admin/dashboard'), {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data.data);
            } else {
                console.error('Error al obtener estadísticas de administración');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const getTipoSocioLabel = (tipo: string) => {
        const labels: { [key: string]: string } = {
            'propietario': 'Propietarios',
            'socio': 'Socios',
            'inversor': 'Inversores',
            'administrador': 'Administradores'
        };
        return labels[tipo] || tipo;
    };

    const getTipoPropiedadLabel = (tipo: string) => {
        const labels: { [key: string]: string } = {
            'habitacion': 'Habitaciones',
            'apartamento': 'Apartamentos',
            'suite': 'Suites'
        };
        return labels[tipo] || tipo;
    };

    const getEstadoPropiedadLabel = (estado: string) => {
        const labels: { [key: string]: string } = {
            'disponible': 'Disponibles',
            'ocupada': 'Ocupadas',
            'mantenimiento': 'Mantenimiento',
            'reservada': 'Reservadas'
        };
        return labels[estado] || estado;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                    <h2 className="text-2xl font-semibold text-gray-600">Panel de Administración</h2>
                    <p className="text-gray-600 mt-2">Gestiona usuarios, propiedades y configuración del sistema</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 md:flex-row md:space-x-3">
                    <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2">
                        <PlusIcon className="h-4 w-4" />
                        <span>Nuevo Usuario</span>
                    </button>
                    <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-2">
                        <PlusIcon className="h-4 w-4" />
                        <span>Nueva Propiedad</span>
                    </button>
                </div>
            </div>

            {/* Estadísticas Generales */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Usuarios</p>
                            <p className="text-2xl font-bold text-gray-900">{stats?.resumen.totalUsers || 0}</p>
                        </div>
                        <UsersIcon className="h-8 w-8 text-blue-600" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Propiedades</p>
                            <p className="text-2xl font-bold text-gray-900">{stats?.resumen.totalProperties || 0}</p>
                        </div>
                        <BuildingOffice2Icon className="h-8 w-8 text-green-600" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Usuarios Activos</p>
                            <p className="text-2xl font-bold text-gray-900">{stats?.resumen.activeUsers || 0}</p>
                        </div>
                        <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Propiedades Activas</p>
                            <p className="text-2xl font-bold text-gray-900">{stats?.resumen.activeProperties || 0}</p>
                        </div>
                        <BuildingOfficeIcon className="h-8 w-8 text-orange-600" />
                    </div>
                </div>
            </div>

            {/* Gráficos de Distribución */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Distribución de Usuarios por Tipo */}
                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center space-x-2 mb-4">
                        <ChartPieIcon className="h-5 w-5 text-blue-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Usuarios por Tipo</h3>
                    </div>
                    <div className="space-y-3">
                        {stats?.distribucion.usuariosPorTipo.map((item) => (
                            <div key={item._id} className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                    {getTipoSocioLabel(item._id)}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {item.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Distribución de Propiedades por Tipo */}
                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center space-x-2 mb-4">
                        <ChartBarIcon className="h-5 w-5 text-green-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Propiedades por Tipo</h3>
                    </div>
                    <div className="space-y-3">
                        {stats?.distribucion.propiedadesPorTipo.map((item) => (
                            <div key={item._id} className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                    {getTipoPropiedadLabel(item._id)}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {item.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Distribución de Propiedades por Estado */}
                <div className="bg-white p-6 rounded-lg shadow-md border">
                    <div className="flex items-center space-x-2 mb-4">
                        <CogIcon className="h-5 w-5 text-orange-600" />
                        <h3 className="text-lg font-semibold text-gray-900">Propiedades por Estado</h3>
                    </div>
                    <div className="space-y-3">
                        {stats?.distribucion.propiedadesPorEstado.map((item) => (
                            <div key={item._id} className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">
                                    {getEstadoPropiedadLabel(item._id)}
                                </span>
                                <span className="text-sm font-semibold text-gray-900">
                                    {item.count}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Acciones Rápidas */}
            <div className="bg-white p-6 rounded-lg shadow-md border">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                            <UsersIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600">Gestionar Usuarios</p>
                    </button>
                    <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                        <BuildingOffice2Icon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600">Gestionar Propiedades</p>
                    </button>
                    <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                        <ChartPieIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600">Ver Reportes</p>
                    </button>
                    <button className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-colors">
                        <CogIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm font-medium text-gray-600">Configuración</p>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;

